import { __awaiter, __extends, __generator, __spreadArrays } from "tslib";
import { debounce, isDefined, isFunction, isString, Platform } from '@tyler-components-web/core';
import { AutocompleteMode, AUTOCOMPLETE_CONSTANTS } from './autocomplete-constants';
import { getSelectedOption, isOptionType, OptionType, optionEqualPredicate } from './autocomplete-utils';
import { randomChars, highlightTextHTML } from '../core';
import { ListDropdownAwareFoundation } from '../list-dropdown/list-dropdown-aware-foundation';
import { ListDropdownAsyncStyle } from '../list-dropdown';
/**
 * The foundation class behind the `<tcw-autocomplete>` element.
 */
var AutocompleteFoundation = /** @class */ (function (_super) {
    __extends(AutocompleteFoundation, _super);
    function AutocompleteFoundation(_adapter) {
        var _this = _super.call(this) || this;
        _this._adapter = _adapter;
        _this._isInitialized = false;
        _this._isDropdownOpen = false;
        _this._mode = AutocompleteMode.Default;
        _this._multiple = false;
        _this._debounce = AUTOCOMPLETE_CONSTANTS.numbers.DEFAULT_DEBOUNCE_TIME;
        _this._allowUnmatched = false;
        _this._filterOnFocus = true;
        _this._options = [];
        _this._filterText = '';
        _this._selectedOptions = [];
        _this._values = [];
        _this._pendingFilterPromises = [];
        _this._selectOnBlur = false;
        _this._matchKey = null;
        _this._clickListener = function (evt) { return _this._onClick(evt); };
        _this._focusListener = function () { return _this._onFocus(); };
        _this._blurListener = function () { return _this._onBlur(); };
        _this._keydownListener = function (evt) { return _this._onKeydown(evt); };
        _this._dropdownIconClickListener = function (evt) { return _this._onDropdownIconClick(evt); };
        _this._dropdownScrollEndListener = function () { return _this._onDropdownScrollEnd(); };
        _this._dropdownIconMouseDownListener = function (evt) { return _this._onDropdownIconMouseDown(evt); };
        _this._dismissListener = function () { return _this._onDismiss(); };
        _this._activeChangeListener = function (id) { return _this._adapter.updateActiveDescendant(id); };
        _this._targetWidthCallback = function () { return _this._adapter.getTargetElementWidth(_this._popupTarget); };
        _this._optionBuilderCallback = function (option, parentElement) {
            return _this._optionBuilder(option, _this._filterText, parentElement);
        };
        _this._identifier = randomChars();
        return _this;
    }
    AutocompleteFoundation.prototype.initialize = function () {
        return __awaiter(this, void 0, void 0, function () {
            var e_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!this._adapter.hasInputElement() && !this._adapter.setInputElement()) {
                            throw new Error('An input element is required as a child of the autocomplete component.');
                        }
                        this._setInputListener();
                        this._setFilterCallback();
                        this._attachListeners();
                        this._initializeAccessibility();
                        if (!this._values.length) return [3 /*break*/, 6];
                        if (!!this._selectedOptions.length) return [3 /*break*/, 5];
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this._executeFilter()];
                    case 2:
                        _a.sent();
                        return [3 /*break*/, 4];
                    case 3:
                        e_1 = _a.sent();
                        console.error(e_1);
                        return [3 /*break*/, 4];
                    case 4:
                        this._updateSelectedOptions(this._values);
                        _a.label = 5;
                    case 5:
                        this._adapter.setSelectedText(this._getSelectedText());
                        _a.label = 6;
                    case 6:
                        this._isInitialized = true;
                        return [2 /*return*/];
                }
            });
        });
    };
    AutocompleteFoundation.prototype.disconnect = function () {
        if (this._isInitialized) {
            this._detachListeners();
            this._isInitialized = false;
        }
        if (this._isDropdownOpen) {
            this._closeDropdown();
        }
    };
    AutocompleteFoundation.prototype._attachListeners = function () {
        this._adapter.addInputListener('click', this._clickListener);
        this._adapter.addInputListener('focus', this._focusListener);
        this._adapter.addInputListener('blur', this._blurListener);
        this._adapter.addInputListener('input', this._inputListener);
        this._adapter.addInputListener('keydown', this._keydownListener);
        this._adapter.setDropdownIconListener('click', this._dropdownIconClickListener);
        this._adapter.setDropdownIconListener('mousedown', this._dropdownIconMouseDownListener);
    };
    AutocompleteFoundation.prototype._detachListeners = function () {
        this._adapter.removeInputListener('click', this._clickListener);
        this._adapter.removeInputListener('focus', this._focusListener);
        this._adapter.removeInputListener('blur', this._blurListener);
        this._adapter.removeInputListener('input', this._inputListener);
        this._adapter.removeInputListener('keydown', this._keydownListener);
        this._adapter.removeDropdownIconListener('click', this._dropdownIconClickListener);
        this._adapter.removeDropdownIconListener('mousedown', this._dropdownIconMouseDownListener);
    };
    AutocompleteFoundation.prototype._setInputListener = function () {
        var _this = this;
        this._inputListener = function (evt) { return _this._onInput(evt); };
    };
    AutocompleteFoundation.prototype._setFilterCallback = function () {
        this._filterFn = isDefined(this._debounce) && this._debounce > 0 ? debounce(this._debounceFilter, this._debounce, false) : this._debounceFilter;
    };
    AutocompleteFoundation.prototype._initializeAccessibility = function () {
        this._adapter.intializeInputAccessibility(this._identifier);
    };
    Object.defineProperty(AutocompleteFoundation.prototype, "_flatOptions", {
        get: function () {
            if (isOptionType(this._options, OptionType.Group)) {
                return this._options.reduce(function (previousValue, currentValue) { return previousValue.concat(currentValue.options); }, []);
            }
            return this._options;
        },
        enumerable: true,
        configurable: true
    });
    AutocompleteFoundation.prototype._onClick = function (evt) {
        if (!this._isDropdownOpen && this._filterOnFocus) {
            this._showDropdown();
        }
    };
    AutocompleteFoundation.prototype._onDropdownIconMouseDown = function (evt) {
        evt.preventDefault(); // Always avoid changing focus when clicking the dropdown
    };
    AutocompleteFoundation.prototype._onDropdownIconClick = function (evt) {
        var _this = this;
        if (!this._isDropdownOpen) {
            this._adapter.focus();
            window.requestAnimationFrame(function () { return _this._showDropdown(); });
        }
        else {
            this._closeDropdown();
        }
    };
    AutocompleteFoundation.prototype._onDropdownScrollEnd = function () {
        this._adapter.emitHostEvent(AUTOCOMPLETE_CONSTANTS.events.SCROLLED_BOTTOM);
    };
    AutocompleteFoundation.prototype._onFocus = function () {
        if (!this._isDropdownOpen && this._adapter.getInputValue() && !Platform.isMobile) {
            this._adapter.selectInputValue();
        }
    };
    AutocompleteFoundation.prototype._onBlur = function () {
        if (this._isDropdownOpen) {
            if (this._selectOnBlur && !this._multiple) {
                this._selectActiveOption(false);
            }
            this._closeDropdown();
        }
        // If we are in stateless mode, we don't need to do anything further
        if (this._mode === AutocompleteMode.Stateless) {
            return;
        }
        if (!this._selectedOptions.length) {
            if (!this._allowUnmatched) {
                this._filterText = '';
                this._adapter.setSelectedText('');
            }
        }
        else {
            this._adapter.setSelectedText(this._getSelectedText());
        }
    };
    AutocompleteFoundation.prototype._onInput = function (evt) {
        if (this._selectedOptions.length && (!this._adapter.getInputValue() || this._allowUnmatched) && !this._adapter.isWrappingChipField()) {
            this._selectedOptions = [];
            this._values = [];
            this._emitChangeEvent();
        }
        this._filterText = this._adapter.getInputValue();
        this._filterFn();
    };
    AutocompleteFoundation.prototype._debounceFilter = function () {
        return __awaiter(this, void 0, void 0, function () {
            var filterText, filterPromise, _a, promiseIndex;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        // There is the potential that the user could have started a filter, then moved focus to another element
                        // while a debounced filter is running. This check detects that scenario and stops all pending filters.
                        if (!this._adapter.hasFocus()) {
                            this._pendingFilterPromises = [];
                            if (this._isDropdownOpen) {
                                this._closeDropdown();
                            }
                            return [2 /*return*/];
                        }
                        filterText = this._filterText;
                        filterPromise = this._executeFilter();
                        // Keep track of all pending promises
                        this._pendingFilterPromises.push(filterPromise);
                        // If the dropdown is open, show the spinner and execute the filter. If not open, then we need to
                        // show the dropdown (which will handle showing the spinner and executing the filter for us) so
                        // we use the promise from that method instead.
                        if (this._isDropdownOpen) {
                            this._adapter.setBusyVisibility(true);
                        }
                        else {
                            this._showDropdown(false);
                        }
                        _b.label = 1;
                    case 1:
                        _b.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, filterPromise];
                    case 2:
                        _b.sent();
                        return [3 /*break*/, 4];
                    case 3:
                        _a = _b.sent();
                        // When an exception occurs, we just flush the pending promises and clean up
                        this._pendingFilterPromises = [];
                        if (this._isDropdownOpen) {
                            this._closeDropdown();
                        }
                        return [2 /*return*/];
                    case 4:
                        // We only continue if the filter text before this result matches the latest filter text
                        if (filterText === this._filterText) {
                            this._pendingFilterPromises = [];
                            this._onFilterComplete();
                        }
                        else {
                            promiseIndex = this._pendingFilterPromises.indexOf(filterPromise);
                            if (promiseIndex !== -1) {
                                this._pendingFilterPromises.splice(promiseIndex, 1);
                            }
                        }
                        return [2 /*return*/];
                }
            });
        });
    };
    AutocompleteFoundation.prototype._onKeydown = function (evt) {
        switch (evt.code) {
            case 'Esc':
            case 'Escape':
                if (this._isDropdownOpen) {
                    evt.preventDefault();
                    this._closeDropdown();
                }
                break;
            case 'Down':
            case 'ArrowDown':
                evt.preventDefault();
                if (!this._isDropdownOpen) {
                    this._showDropdown();
                }
                else {
                    this._adapter.propagateKey(evt.code);
                }
                break;
            case 'Up':
            case 'ArrowUp':
                evt.preventDefault();
                if (this._isDropdownOpen) {
                    this._adapter.propagateKey(evt.code);
                }
                break;
            case 'Enter':
            case 'Home':
            case 'End':
                evt.preventDefault();
                this._adapter.propagateKey(evt.code);
                break;
            case 'Backspace':
            case 'Delete':
                var input = evt.target;
                var value = this._adapter.getInputValue();
                var isRemovingAllChars = input.value.substring(input.selectionStart, input.selectionEnd) === input.value;
                var isBackspacingLastChar = value.length === 1 && input.selectionEnd === 1;
                var isDeletingLastChar = value.length === 1 && input.selectionEnd === 0;
                var isEmpty = !value || isRemovingAllChars || isBackspacingLastChar || isDeletingLastChar;
                if (!this._adapter.isWrappingChipField() && isEmpty && this._values.length) {
                    this._selectedOptions = [];
                    this._values = [];
                    if (this._isDropdownOpen) {
                        this._adapter.setSelectedOptions([]);
                        if (this._selectOnBlur) {
                            this._adapter.clearActiveOption();
                        }
                    }
                    this._emitChangeEvent();
                }
                break;
        }
    };
    AutocompleteFoundation.prototype._executeFilter = function (sendFilterText, sendValue) {
        var _this = this;
        if (sendFilterText === void 0) { sendFilterText = true; }
        if (sendValue === void 0) { sendValue = false; }
        if (!this._filter || typeof this._filter !== 'function') {
            throw new Error('A filter callback must be provided. Did you set the "filter" property?');
        }
        var filterText = sendFilterText ? this._filterText : '';
        var value = sendValue ? this._getValue() : null;
        return new Promise(function (resolve, reject) {
            return Promise.resolve(_this._filter(filterText, value))
                .then(function (options) {
                _this._options = options;
                resolve(_this._options);
            })
                .catch(function (e) { return reject("An unexpected error occurred while filtering: " + e); });
        });
    };
    AutocompleteFoundation.prototype._onFilterComplete = function () {
        if (!this._adapter.hasFocus()) {
            if (this._isDropdownOpen) {
                this._closeDropdown();
            }
            return;
        }
        if (this._options.length) {
            var sendFilterText = this._allowUnmatched && !this._selectedOptions.length;
            this._dropdownReady(sendFilterText);
        }
        else {
            this._closeDropdown();
        }
    };
    AutocompleteFoundation.prototype._showDropdown = function (filter, userTriggered) {
        if (filter === void 0) { filter = true; }
        if (userTriggered === void 0) { userTriggered = true; }
        return __awaiter(this, void 0, void 0, function () {
            var sendFilterText, config, e_2;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        sendFilterText = this._allowUnmatched && !this._selectedOptions.length;
                        this._isDropdownOpen = true;
                        config = {
                            options: this._options,
                            multiple: this._multiple,
                            selectedValues: __spreadArrays(this._values),
                            id: "tcw-autocomplete-" + this._identifier,
                            asyncStyle: ListDropdownAsyncStyle.Skeleton,
                            optionLimit: this._optionLimit,
                            popupClasses: this._popupClasses,
                            headerBuilder: this._popupHeaderBuilder,
                            footerBuilder: this._popupFooterBuilder,
                            transform: function (label) {
                                if (_this._filterText) {
                                    // Highlight the filter text within the label
                                    var highlightElement = highlightTextHTML(label, _this._filterText);
                                    if (highlightElement) {
                                        return highlightElement;
                                    }
                                }
                                return label;
                            },
                            allowBusy: true,
                            optionBuilder: this._optionBuilder !== undefined ? this._optionBuilderCallback : undefined,
                            syncWidth: this._syncPopupWidth,
                            highlightFirst: this._highlightFirst && !this._selectOnBlur,
                            observeScroll: this._observeScroll,
                            observeScrollThreshold: this._observeScrollThreshold,
                            dialogWidthThreshold: 0,
                            scrollEndListener: this._dropdownScrollEndListener,
                            activeChangeCallback: this._activeChangeListener,
                            targetWidthCallback: this._targetWidthCallback,
                            selectCallback: function (value) { return _this._onSelect(value); },
                            closeCallback: function () { return _this._closeDropdown(); }
                        };
                        this._adapter.show(config, this._popupTarget);
                        if (!filter) return [3 /*break*/, 5];
                        if (this._options.length) {
                            this._adapter.setBusyVisibility(true);
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this._executeFilter(sendFilterText)];
                    case 2:
                        _a.sent();
                        return [3 /*break*/, 4];
                    case 3:
                        e_2 = _a.sent();
                        console.error(e_2);
                        return [3 /*break*/, 4];
                    case 4:
                        this._updateSelectedOptions(this._values);
                        _a.label = 5;
                    case 5:
                        // If we have any pending filter promises, then we will let those complete and update the dropdown separately after
                        if (this._pendingFilterPromises.length) {
                            return [2 /*return*/];
                        }
                        this._dropdownReady(userTriggered);
                        return [2 /*return*/];
                }
            });
        });
    };
    AutocompleteFoundation.prototype._dropdownReady = function (userTriggered) {
        if (userTriggered === void 0) { userTriggered = true; }
        if (!this._isDropdownOpen || !this._options.length || (userTriggered && !this._adapter.hasFocus())) {
            this._closeDropdown();
            return;
        }
        this._sortSelectedOptions();
        this._adapter.setBusyVisibility(false);
        this._adapter.setOptions(this._options);
        if (this._highlightFirst) {
            this._adapter.activateInitialOption();
        }
        this._adapter.setDismissListener(this._dismissListener);
    };
    AutocompleteFoundation.prototype._closeDropdown = function () {
        if (this._multiple) {
            this._filterText = '';
        }
        this._isDropdownOpen = false;
        this._adapter.hide(this._dismissListener);
        this._sortSelectedOptions();
    };
    AutocompleteFoundation.prototype._sortSelectedOptions = function () {
        var _this = this;
        var isUsingComparatorOverride = isFunction(this._optionComparator);
        // When in multiple selection mode, we need to make sure that the selected options appear at the top of the list
        //  and that options are sorted
        // NOTE: Only autocompletes with multiselect enabled will be sorted.
        //  We _should_ allow the optionComparator to allow custom sorting for single selection mode as well...
        //  but would be a breaking change in 1.x. Should add this in 2.0
        // NOTE: When not using a comparator override, the options are only sorted when at least one option is selected.
        //  This can lead to different option orders depending on selection state. This is another item to address in 2.0
        if (this._multiple && isOptionType(this._options, OptionType.Option) && (isUsingComparatorOverride || this._selectedOptions.length > 0)) {
            var selectedOptions_1 = [];
            var unselectedOptions_1 = [];
            this._options.forEach(function (option) {
                if (_this._selectedOptions.find(function (o) { return optionEqualPredicate(o, option.value, _this._matchKey); })) {
                    selectedOptions_1.push(option);
                }
                else {
                    unselectedOptions_1.push(option);
                }
            });
            var defaultComparator = function (a, b) { return a.label.localeCompare(b.label); };
            var comparator = isUsingComparatorOverride ? this._optionComparator : defaultComparator;
            selectedOptions_1.sort(comparator);
            unselectedOptions_1.sort(comparator);
            this._options = __spreadArrays(selectedOptions_1, unselectedOptions_1);
        }
    };
    /**
     * Handles selecting an item in the dropdown.
     * @param {string} selectedValue The string value to select.
     */
    AutocompleteFoundation.prototype._onSelect = function (selectedValue, keepFocus) {
        if (keepFocus === void 0) { keepFocus = true; }
        return __awaiter(this, void 0, void 0, function () {
            var select, shouldContinue;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (this._valueChanging) {
                            return [2 /*return*/];
                        }
                        // If we are in stateless mode, then we need to just emit an event when selecting an option. The
                        // selected values are not tracked in stateless mode
                        if (this._mode === AutocompleteMode.Stateless) {
                            this._filterText = '';
                            this._closeDropdown();
                            this._adapter.emitHostEvent(AUTOCOMPLETE_CONSTANTS.events.SELECT, { value: selectedValue }, true);
                            return [2 /*return*/];
                        }
                        select = function () {
                            var flatOptions = _this._flatOptions;
                            var option = flatOptions.find(function (o) { return optionEqualPredicate(o, selectedValue, _this._matchKey); });
                            var value = option ? option.value : '';
                            var label = option ? option.label : '';
                            if (_this._multiple) {
                                var optionIndex = flatOptions.findIndex(function (o) { return optionEqualPredicate(o, selectedValue, _this._matchKey); });
                                if (optionIndex >= 0) {
                                    var selected = _this._selectedOptions.some(function (o) { return optionEqualPredicate(o, selectedValue, _this._matchKey); });
                                    _this._adapter.toggleOptionMultiple(optionIndex, selected);
                                }
                                var selectedOption = getSelectedOption(_this._selectedOptions, value);
                                if (selectedOption) {
                                    var index = _this._selectedOptions.indexOf(selectedOption);
                                    _this._selectedOptions.splice(index, 1);
                                }
                                else {
                                    _this._selectedOptions.push(option);
                                }
                            }
                            else {
                                if (isDefined(value)) {
                                    _this._selectedOptions[0] = option;
                                    _this._filterText = label;
                                }
                                else {
                                    _this._selectedOptions = [];
                                    _this._filterText = '';
                                }
                            }
                            // Update the dropdown selection status if its open
                            if (_this._isDropdownOpen) {
                                _this._adapter.setSelectedOptions(_this._selectedOptions);
                            }
                            // Keep the values in sync with the currently selected options
                            _this._values = _this._selectedOptions.map(function (o) { return o.value; });
                            // Update the selected text in the input
                            _this._adapter.setSelectedText(_this._getSelectedText());
                            // Select the text in the input to allow for the next filter
                            if (!Platform.isMobile && keepFocus) {
                                _this._adapter.selectInputValue();
                            }
                            if (_this._multiple) {
                                // If we're in multiselect mode, we need to toggle the selected option
                                var index = flatOptions.findIndex(function (o) { return optionEqualPredicate(o, selectedValue, _this._matchKey); });
                                var isSelected = _this._values.includes(selectedValue);
                                _this._adapter.toggleOptionMultiple(index, isSelected);
                            }
                            _this._emitChangeEvent();
                        };
                        // We close the dropdown immediately if in single selection mode
                        if (this._isDropdownOpen && !this._multiple) {
                            this._closeDropdown();
                        }
                        if (!(typeof this._beforeValueChange === 'function')) return [3 /*break*/, 2];
                        this._valueChanging = Promise.resolve(this._beforeValueChange.call(null, selectedValue, this._matchKey));
                        return [4 /*yield*/, this._valueChanging];
                    case 1:
                        shouldContinue = _a.sent();
                        if (shouldContinue) {
                            select();
                        }
                        this._valueChanging = undefined;
                        return [3 /*break*/, 3];
                    case 2:
                        select();
                        _a.label = 3;
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    AutocompleteFoundation.prototype._selectActiveOption = function (keepFocus) {
        if (keepFocus === void 0) { keepFocus = true; }
        var activeOptionIndex = this._adapter.getActiveOptionIndex();
        if (typeof activeOptionIndex === 'number' && activeOptionIndex >= 0) {
            var option = this._flatOptions[activeOptionIndex];
            if (option) {
                this._onSelect(option.value, keepFocus);
            }
        }
    };
    AutocompleteFoundation.prototype._emitChangeEvent = function () {
        this._adapter.emitHostEvent(AUTOCOMPLETE_CONSTANTS.events.CHANGE, this._getValue(), true);
    };
    /**
     * Retrieves the current value(s) from the selected options array based on whether
     * we are in multi-select mode or not.
     */
    AutocompleteFoundation.prototype._getValue = function () {
        if (!this._values) {
            return null;
        }
        if (!this._values.length) {
            return this._multiple ? [] : null;
        }
        return this._multiple ? __spreadArrays(this._values) : this._values[0];
    };
    /**
     * Creates the selected text value from the selected label values.
     */
    AutocompleteFoundation.prototype._getSelectedText = function () {
        if (this._adapter.isWrappingChipField()) {
            return '';
        }
        if (this._selectedTextBuilder) {
            return this._selectedTextBuilder(this._selectedOptions);
        }
        else {
            if (this._multiple) {
                if (this._values.length) {
                    return this._values.length + " " + (this._values.length === 1 ? 'option' : 'options') + " selected";
                }
                else {
                    return '';
                }
            }
            else {
                return this._selectedOptions.filter(function (o) { return o && o.label; }).map(function (o) { return o.label; }).join(' ').trim();
            }
        }
    };
    /**
     * Handles the user dismissing the dropdown. This is only called if the blur event was triggered
     * from within the popup element itself (in a custom template most likely).
     * @param keepFocus Keep focus on the dropdown or not.
     */
    AutocompleteFoundation.prototype._onDismiss = function () {
        this._closeDropdown();
    };
    AutocompleteFoundation.prototype._applyValue = function (value) {
        return __awaiter(this, void 0, void 0, function () {
            var values, allOptions, e_3;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        values = [];
                        this._selectedOptions = [];
                        if (!Array.isArray(value)) {
                            values = isDefined(value) ? [value] : [];
                        }
                        else {
                            values = value;
                        }
                        // If this is not a multi-select, then make sure we only allow one selected value
                        if (!this._multiple && values.length > 1) {
                            values = [values[0]];
                        }
                        if (isOptionType(values, OptionType.Option)) {
                            this._values = values.map(function (o) { return o.value; });
                            this._selectedOptions = values;
                        }
                        else {
                            this._values = values;
                        }
                        allOptions = this._flatOptions;
                        if (values.length && allOptions.length) {
                            this._updateSelectedOptions(values);
                        }
                        if (!(this._values.length && !this._selectedOptions.length && this._filter && this._isInitialized)) return [3 /*break*/, 5];
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this._executeFilter(false, true)];
                    case 2:
                        _a.sent();
                        return [3 /*break*/, 4];
                    case 3:
                        e_3 = _a.sent();
                        console.error(e_3);
                        return [3 /*break*/, 4];
                    case 4:
                        this._updateSelectedOptions(this._values);
                        _a.label = 5;
                    case 5:
                        // Update filter text to match the label when on in multiselect mode
                        if (!this._multiple) {
                            this._filterText = this._selectedOptions.length ? this._selectedOptions[0].label : '';
                        }
                        else {
                            this._filterText = '';
                        }
                        // Update the state of the component based on the existence of a selected value
                        if (this._isInitialized) {
                            this._adapter.setSelectedText(this._getSelectedText());
                        }
                        // When the value is changed programatically we should update the selected options
                        if (this._isDropdownOpen) {
                            this._adapter.setSelectedOptions(this._selectedOptions);
                        }
                        return [2 /*return*/];
                }
            });
        });
    };
    AutocompleteFoundation.prototype._updateSelectedOptions = function (values) {
        var _this = this;
        if (this._selectedOptions.length) {
            this._selectedOptions = [];
        }
        var flatOptions = this._flatOptions;
        if (isOptionType(values, OptionType.Option)) {
            var _loop_1 = function (option) {
                var actualOption = flatOptions.find(function (o) { return optionEqualPredicate(o, option.value, _this._matchKey); });
                if (actualOption) {
                    this_1._selectedOptions.push(actualOption);
                }
                else {
                    this_1._selectedOptions.push(option);
                }
            };
            var this_1 = this;
            for (var _i = 0, _a = values; _i < _a.length; _i++) {
                var option = _a[_i];
                _loop_1(option);
            }
        }
        else {
            var _loop_2 = function (value) {
                var actualOption = flatOptions.find(function (o) { return optionEqualPredicate(o, value, _this._matchKey); });
                if (actualOption) {
                    this_2._selectedOptions.push(actualOption);
                }
                else if (this_2._allowUnmatched) {
                    this_2._selectedOptions.push({ label: value, value: value });
                }
            };
            var this_2 = this;
            for (var _b = 0, _c = values; _b < _c.length; _b++) {
                var value = _c[_b];
                _loop_2(value);
            }
        }
    };
    Object.defineProperty(AutocompleteFoundation.prototype, "mode", {
        /** Gets/sets the mode state. */
        get: function () {
            return this._mode;
        },
        set: function (value) {
            if (this._mode !== value) {
                this._mode = value;
                if (this._mode === AutocompleteMode.Stateless) {
                    this._selectedOptions = [];
                    if (this._isDropdownOpen) {
                        this._closeDropdown();
                    }
                }
                this._adapter.setHostAttribute(AUTOCOMPLETE_CONSTANTS.attributes.MODE, this._mode);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AutocompleteFoundation.prototype, "multiple", {
        /** Gets/sets the multi-select state. */
        get: function () {
            return this._multiple;
        },
        set: function (value) {
            if (this._multiple !== value) {
                this._multiple = value;
                this._adapter.setHostAttribute(AUTOCOMPLETE_CONSTANTS.attributes.MULTIPLE, isDefined(this._multiple) ? this._multiple.toString() : '');
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AutocompleteFoundation.prototype, "value", {
        /** Gets/sets the value of the component. */
        get: function () {
            return this._getValue();
        },
        set: function (value) {
            var _this = this;
            var values = [];
            if (value === null || value === undefined) {
                values = [];
            }
            else if (Array.isArray(value)) {
                values = JSON.parse(JSON.stringify(value));
            }
            else if (isString(value)) {
                values = [value];
            }
            else {
                values = [JSON.parse(JSON.stringify(value))];
            }
            var hasNewValue = values.length !== this._values.length || values.some(function (v) { return !_this._values.includes(v); });
            if (!hasNewValue) {
                return;
            }
            // We only apply the new values if they have actually changed
            this._applyValue(value);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AutocompleteFoundation.prototype, "highlightFirst", {
        /** Gets/sets the highlight first option setting. */
        get: function () {
            return this._highlightFirst;
        },
        set: function (value) {
            if (this._highlightFirst !== value) {
                this._highlightFirst = value;
                this._adapter.setHostAttribute(AUTOCOMPLETE_CONSTANTS.attributes.HIGHLIGHT_FIRST, isDefined(this._highlightFirst) ? this._highlightFirst.toString() : '');
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AutocompleteFoundation.prototype, "filterOnFocus", {
        /** Gets/sets filter on focus settings which controls whether the dropdown displays automatically when focused. */
        get: function () {
            return this._filterOnFocus;
        },
        set: function (value) {
            if (this._filterOnFocus !== value) {
                this._filterOnFocus = value;
                this._adapter.setHostAttribute(AUTOCOMPLETE_CONSTANTS.attributes.FILTER_ON_FOCUS, isDefined(this._filterOnFocus) ? this._filterOnFocus.toString() : '');
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AutocompleteFoundation.prototype, "allowUnmatched", {
        /** Controls whether unmatched text entered by the user will stay visible an option in the dropdown is not found. */
        get: function () {
            return this._allowUnmatched;
        },
        set: function (value) {
            if (this._allowUnmatched !== value) {
                this._allowUnmatched = value;
                if (isDefined(this._allowUnmatched)) {
                    this._adapter.setHostAttribute(AUTOCOMPLETE_CONSTANTS.attributes.ALLOW_UNMATCHED, this._allowUnmatched.toString());
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AutocompleteFoundation.prototype, "matchKey", {
        /** Gets/sets the property key to match the value to an option.  */
        get: function () {
            return this._matchKey;
        },
        set: function (value) {
            if (this._matchKey !== value) {
                this._matchKey = value;
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AutocompleteFoundation.prototype, "popupTarget", {
        /** Gets/sets the selector that will be used to find an element to attach the popup to. Defaults to the input element. */
        get: function () {
            return this._popupTarget;
        },
        set: function (value) {
            if (this._popupTarget !== value) {
                this._popupTarget = value;
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AutocompleteFoundation.prototype, "popupClasses", {
        /** Gets/sets the list of classes to apply to the popup element. */
        get: function () {
            return Array.isArray(this._popupClasses) ? __spreadArrays(this._popupClasses) : [this._popupClasses];
        },
        set: function (value) {
            if (this._popupClasses !== value) {
                this._popupClasses = Array.isArray(value) ? __spreadArrays(value) : [value];
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AutocompleteFoundation.prototype, "popupHeaderBuilder", {
        /** Gets/sets the builder callback to use for the popup header. */
        set: function (value) {
            this._popupHeaderBuilder = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AutocompleteFoundation.prototype, "popupFooterBuilder", {
        /** Gets/sets the builder callback to use for the popup footer. */
        set: function (value) {
            this._popupFooterBuilder = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AutocompleteFoundation.prototype, "syncPopupWidth", {
        /** Gets/sets whether the popup width is synchronized with the popup target. */
        get: function () {
            return this._syncPopupWidth;
        },
        set: function (value) {
            if (this._syncPopupWidth !== value) {
                this._syncPopupWidth = value;
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AutocompleteFoundation.prototype, "optionLimit", {
        /** Gets/sets the maximum number of options to display in the dropdown. */
        get: function () {
            return this._optionLimit;
        },
        set: function (value) {
            if (this._optionLimit !== value) {
                this._optionLimit = value;
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AutocompleteFoundation.prototype, "debounce", {
        /** Gets/sets the debounce delay (milliseconds) for keyboard events. */
        get: function () {
            return this._debounce;
        },
        set: function (value) {
            if (this._debounce !== value) {
                this._debounce = value;
                if (this._isInitialized) {
                    this._setFilterCallback();
                }
                this._adapter.setHostAttribute(AUTOCOMPLETE_CONSTANTS.attributes.DEBOUNCE, isDefined(this._debounce) ? this._debounce.toString() : '');
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AutocompleteFoundation.prototype, "optionBuilder", {
        /** Sets the item builder callback that will be executed when building the option list in the dropdown. */
        get: function () {
            return this._optionBuilder;
        },
        set: function (fn) {
            this._optionBuilder = fn;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AutocompleteFoundation.prototype, "filter", {
        /** Sets the filter callback that will be executed when fetching options for the autocomplete dropdown. */
        get: function () {
            return this._filter;
        },
        set: function (cb) {
            var _this = this;
            if (this._filter !== cb) {
                this._filter = cb;
                // If we have a value, but don't have any options yet then execute the filter
                if (this._isInitialized && this._values.length && !this._flatOptions.length && this._filter) {
                    this._executeFilter().then(function () {
                        _this._updateSelectedOptions(_this._values);
                        _this._adapter.setSelectedText(_this._getSelectedText());
                    });
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AutocompleteFoundation.prototype, "selectedTextBuilder", {
        /** Sets the selected text builder callback that will be executed when getting the selected text. */
        get: function () {
            return this._selectedTextBuilder;
        },
        set: function (fn) {
            this._selectedTextBuilder = fn;
            // If there are selected options, set the selected text again
            if (this._selectedOptions.length) {
                this._adapter.setSelectedText(this._getSelectedText());
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AutocompleteFoundation.prototype, "optionComparator", {
        /** Sets the option comparator that will be executed when sorting the option list in the dropdown. */
        get: function () {
            return this._optionComparator;
        },
        set: function (fn) {
            this._optionComparator = fn;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AutocompleteFoundation.prototype, "observeScroll", {
        get: function () {
            return this._observeScroll;
        },
        set: function (value) {
            this._observeScroll = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AutocompleteFoundation.prototype, "observeScrollThreshold", {
        get: function () {
            return this._observeScrollThreshold;
        },
        set: function (value) {
            this._observeScrollThreshold = value;
        },
        enumerable: true,
        configurable: true
    });
    AutocompleteFoundation.prototype.appendOptions = function (options) {
        if (!this._isDropdownOpen) {
            return;
        }
        this._options = __spreadArrays(this._options, options);
        this._adapter.appendOptions(options);
    };
    Object.defineProperty(AutocompleteFoundation.prototype, "isInitialized", {
        get: function () {
            return this._isInitialized;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AutocompleteFoundation.prototype, "open", {
        get: function () {
            return this._isDropdownOpen;
        },
        set: function (value) {
            if (this._isDropdownOpen !== value) {
                if (value) {
                    this._showDropdown(undefined, false);
                }
                else {
                    this._closeDropdown();
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AutocompleteFoundation.prototype, "selectOnBlur", {
        get: function () {
            return this._selectOnBlur;
        },
        set: function (value) {
            if (this._selectOnBlur !== value) {
                this._selectOnBlur = value;
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AutocompleteFoundation.prototype, "beforeValueChange", {
        get: function () {
            return this._beforeValueChange;
        },
        set: function (value) {
            if (value !== this._beforeValueChange) {
                this._beforeValueChange = value;
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AutocompleteFoundation.prototype, "popupElement", {
        get: function () {
            return this._adapter.getPopupElement();
        },
        enumerable: true,
        configurable: true
    });
    return AutocompleteFoundation;
}(ListDropdownAwareFoundation));
export { AutocompleteFoundation };
